"use client"

import styled from "@emotion/styled"
import { FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material"
import {
  Int32StringValueTuple,
  LostFoundColorPublicResponse,
  LostFoundContentPublicResponse,
} from "api"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useState } from "react"
import Container from "ui/Container"
import { applicationRequestClick } from "utils"
import Loading from "../../Common/Loading"
import Result from "../components/Result"
import Complaint from "../Forms/Complaint"
import LostFound from "../Forms/Lost&Found"
import OptimizationProposal from "../Forms/OptimizationProposal"
import Ravkav from "../Forms/Ravkav"
import PenaltyLookup from "../Forms/PenaltyLookup"
import RequestForCredit from "../Forms/RequestForCredit"
import RequestForTaxInvoice from "../Forms/RequestFotTaxInvoice"

const OuterLayout = styled.div`
  width: 100%;
  padding-top: 40px;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding-top: 88px;
  }
`
const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 32px;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const StyledFormLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
  }
`

const StyledFormControlLabel = styled(FormControlLabel)`
  font-size: 18px;
  line-height: 26px;

  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
    border-radius: 4px;
  }

  .MuiRadio-root:focus-visible {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }
`

const StyledRadio = styled(Radio)`
  &:focus-visible img {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }

  input:focus-visible + * img {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }

  &:focus-within img {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }

  &.Mui-focusVisible img {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }
`

const englishLabels = [
  "Complaint",
  "Comment, suggestion, inquiry",
  "Report a Lost Item",
  "Refund request - Rav-Kav",
  "Retrieve Penalties (penalty tariff)",
  "Request credit card refund",
  "Request tax invoice",
]

type FormId = 1 | 2 | 3 | 4 | 5 | 6 | 7
type FormError = "notSubmitted" | "yes" | "no"

interface InquiriesAndRequestsBlockProps {
  data: any
  locale: string
  contactTypesData?: Int32StringValueTuple[]
  lostFoundContentData?: LostFoundContentPublicResponse[]
  lostFoundColorsData?: LostFoundColorPublicResponse[]
  formDynamicTextTooltip?: string
}

export default function InquiriesAndRequestsBlock({
  locale,
  contactTypesData = [],
  lostFoundContentData = [],
  lostFoundColorsData = [],
  formDynamicTextTooltip = "",
}: InquiriesAndRequestsBlockProps) {
  const typedLocale = locale as "he" | "en" | "ar" | "ru"
  const hebrew = typedLocale === "he"
  const tInner = useTranslations("InnerPages")

  // Filter out form type 8 (Lost Item Status) for standalone block
  const filteredContactTypesData = contactTypesData.filter(
    (ct) => ct.item1 !== 8,
  )

  const adjustedContactTypesData = hebrew
    ? filteredContactTypesData.map((ct, idx) =>
        idx === 1 ? { ...ct, item2: "פנייה, הצעה, שבח" } : ct,
      )
    : filteredContactTypesData

  const [isLoading, setIsLoading] = useState(false)
  const [subject, setSubject] = useState<FormId | "">("")
  const [isFormError, setIsFormError] = useState<FormError>("notSubmitted")
  const [resultId, setResultId] = useState<string>()

  const handleResult = (type: FormError, id?: string) => {
    setIsFormError(type)
    setResultId(id)
  }

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value) as FormId
    setSubject(value)

    const originalIndex = contactTypesData.findIndex((ct) => ct.item1 === value)
    applicationRequestClick(
      String(contactTypesData[originalIndex]?.item2) || "",
      englishLabels[originalIndex] || "",
    )
  }

  if (isFormError !== "notSubmitted") {
    return (
      <StyledLayout>
        <Result isFormError={isFormError} id={resultId} />
      </StyledLayout>
    )
  }

  return (
    <OuterLayout>
      <Container paddingMobile>
        <StyledLayout>
          <StyledFormLayout>
            <Typography variant="h4" id="inquiries-radio-buttons-group-label">
              {tInner("inquirySubject")}
            </Typography>

            <StyledRadioGroup
              aria-labelledby="inquiries-radio-buttons-group-label"
              value={subject}
              onChange={handleSubjectChange}
              name="inquiries-radio-buttons-group"
            >
              {adjustedContactTypesData.map((el, i) => (
                <StyledFormControlLabel
                  key={el.item1}
                  value={el.item1}
                  control={
                    <StyledRadio
                      disableRipple
                      icon={
                        <Image
                          width={24}
                          height={24}
                          src="/icons/radioEmpty.svg"
                          alt=""
                        />
                      }
                      checkedIcon={
                        <Image
                          width={24}
                          height={24}
                          src="/icons/radioFull.svg"
                          alt=""
                        />
                      }
                    />
                  }
                  label={hebrew ? el.item2 : englishLabels[i]}
                />
              ))}
            </StyledRadioGroup>

            {isLoading ? (
              <Loading />
            ) : (
              <>
                {subject === 1 && (
                  <Complaint
                    handleResult={handleResult}
                    locale={typedLocale}
                    contactType={1}
                    setIsLoading={setIsLoading}
                    label={adjustedContactTypesData[0]?.item2}
                    tooltipText={formDynamicTextTooltip}
                  />
                )}
                {subject === 2 && (
                  <OptimizationProposal
                    handleResult={handleResult}
                    locale={typedLocale}
                    contactType={2}
                    setIsLoading={setIsLoading}
                    label={adjustedContactTypesData[1]?.item2}
                    tooltipText={formDynamicTextTooltip}
                  />
                )}
                {subject === 3 && (
                  <LostFound
                    handleResult={handleResult}
                    locale={typedLocale}
                    lostFoundContentData={lostFoundContentData}
                    lostFoundColorsData={lostFoundColorsData}
                    setIsLoading={setIsLoading}
                    label={adjustedContactTypesData[2]?.item2}
                    tooltipText={formDynamicTextTooltip}
                  />
                )}
                {subject === 4 && (
                  <Ravkav
                    handleResult={handleResult}
                    locale={typedLocale}
                    contactType={4}
                    setIsLoading={setIsLoading}
                    label={adjustedContactTypesData[3]?.item2}
                    tooltipText={formDynamicTextTooltip}
                  />
                )}
                {subject === 5 && (
                  <PenaltyLookup
                    locale={typedLocale}
                    label={adjustedContactTypesData[4]?.item2}
                  />
                )}
                {subject === 6 && (
                  <RequestForCredit
                    handleResult={handleResult}
                    locale={typedLocale}
                    contactType={6}
                    setIsLoading={setIsLoading}
                    label={adjustedContactTypesData[5]?.item2}
                    tooltipText={formDynamicTextTooltip}
                  />
                )}
                {subject === 7 && (
                  <RequestForTaxInvoice
                    handleResult={handleResult}
                    locale={typedLocale}
                    contactType={7}
                    label={adjustedContactTypesData[6]?.item2}
                    setIsLoading={setIsLoading}
                    tooltipText={formDynamicTextTooltip}
                  />
                )}
              </>
            )}
          </StyledFormLayout>
        </StyledLayout>
      </Container>
    </OuterLayout>
  )
}
